import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatMap, map, type Observable, of, take, tap } from 'rxjs';

import { Toast, ToastService } from '@evc/web-components';
import { I18nService } from '@shared/services/i18n/i18n.service';

import * as ToastActions from '@app/store/actions/toast/toast.action';
import type { ExtraParams } from '@app/types/toast/toast.model';

type ToastActionPayload = {
  message: string;
  params?: Record<string, any>;
  extra: ExtraParams;
};
@Injectable()
export class ToastEffects {
  readonly #actions$ = inject(Actions);

  addSuccessToast$: any = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(ToastActions.addToastSuccess),
        concatMap(this.#parsePayload.bind(this)),
        tap(([message, payload]) => this.#toastService.success(message, payload)),
      ),
    { dispatch: false },
  );

  addErrorToast$: any = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(ToastActions.addToastError),
        concatMap(this.#parsePayload.bind(this)),
        tap(([message, payload]) => this.#toastService.error(message, payload)),
      ),
    { dispatch: false },
  );

  addWarningToast$: any = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(ToastActions.addToastWarning),
        concatMap(this.#parsePayload.bind(this)),
        tap(([message, payload]) => this.#toastService.warning(message, payload)),
      ),
    { dispatch: false },
  );

  addInfoToast$: any = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(ToastActions.addToastInfo),
        concatMap(this.#parsePayload.bind(this)),
        tap(([message, payload]) => this.#toastService.info(message, payload)),
      ),
    { dispatch: false },
  );

  #i18nService = inject(I18nService);
  #toastService = inject(ToastService);

  #parsePayload({
    message,
    params,
    extra,
  }: ToastActionPayload): Observable<[message: string, payload?: Partial<Toast>]> {
    const ready$: Observable<unknown> = this.#i18nService.langReady()
      ? of(undefined)
      : this.#i18nService.onReady$.pipe(take(1));

    return ready$.pipe(
      map(() => [
        this.#i18nService.t(message, params),
        extra as Partial<Toast>,
      ]),
    );
  }
}
